/**
 * In-run progression tally: kill XP/gold as enemies die, the main hero's provisional level, and
 * the purse. Pure (no DOM) so it is unit-tested against applyRunResult.
 *
 * The session feeds it each sim tick's events while the dead units are still in the world. It
 * calls progression's `killRewardsFromEvents` on that tick's new deaths for the entries (unit, npc,
 * corpse position, boss flags), then re-derives each kill's exact reward with `killReward` and
 * rounds with ONE running carry across the whole run. Summing per-tick `killRewardsFromEvents`
 * results instead would round each slice on its own (a 0.68 XP trash kill pops as 1 every time), so
 * the run would pay noticeably more than `killRewardsFromEvents(allEvents)`. With the carry, the pops
 * add up to exactly what the end-of-run `applyRunResult({ kills: tally.summary() })` grants.
 */
import type { ContentRegistry, SimEvent, UnitId, World } from '@keystone/sim';
import {
  type CharacterProgress,
  type KillRewardEntry,
  type KillRewardSummary,
  type Profile,
  MAX_HERO_LEVEL,
  addHeroXp,
  killReward,
  killRewardsFromEvents,
  xpToNext,
} from '@keystone/progression';

/** What the HUD needs from the profile when a run starts. */
export interface InRunStart {
  /** The hero whose XP bar is shown (the profile's main class). */
  classId: string;
  heroName?: string;
  level: number;
  /** XP into the current level. */
  xp: number;
  /** Talent points before the run. */
  talentPoints: number;
  gold: number;
  shards: number;
}

/** Snapshot the main hero + purse from the profile (call right before the run starts). */
export function inRunStart(profile: Profile, heroName?: string): InRunStart {
  const ch = profile.characters[profile.mainClassId];
  return {
    classId: profile.mainClassId,
    heroName,
    level: ch?.hero.level ?? 1,
    xp: ch?.hero.xp ?? 0,
    talentPoints: ch?.hero.talentPoints ?? 1,
    gold: profile.currencies.gold ?? 0,
    shards: profile.currencies.shards ?? 0,
  };
}

/** The hero's provisional progress: start + kill XP so far (completion/timed bonuses come at the end). */
export interface HeroProgressView {
  level: number;
  xp: number;
  /** XP needed for the next level (0 at max level). */
  next: number;
  maxed: boolean;
  talentPoints: number;
}

/** One tick's worth of new rewards. */
export interface TallyTick {
  /** New rewarded kills, with carry-rounded xp/gold (they sum to the run total). */
  entries: KillRewardEntry[];
  xp: number;
  gold: number;
  /** Levels gained this tick (0 almost always). */
  levelsGained: number;
  hero: HeroProgressView;
}

type RewardContent = Pick<ContentRegistry, 'npc' | 'encounter' | 'dungeon'>;

export class InRunTally {
  /** Rounded run totals (what the end screen and applyRunResult see). */
  xp = 0;
  gold = 0;
  /** False once reward computation threw; the run end then lets progression estimate kills. */
  ok = true;
  readonly entries: KillRewardEntry[] = [];
  private exactXp = 0;
  private exactGold = 0;
  private readonly seen = new Set<UnitId>();
  private heroView: HeroProgressView;

  constructor(
    private readonly content: RewardContent,
    readonly keyLevel: number,
    readonly start: InRunStart | null = null,
  ) {
    this.heroView = this.computeHero();
  }

  get hero(): HeroProgressView {
    return this.heroView;
  }

  /** Gold in the purse including this run's kills so far (provisional). */
  get purseGold(): number {
    return (this.start?.gold ?? 0) + this.gold;
  }

  /** The kill rewards to hand to applyRunResult (null when the tally failed). */
  summary(): KillRewardSummary | null {
    return this.ok ? { entries: this.entries.slice(), xp: this.xp, gold: this.gold } : null;
  }

  /**
   * Fold one tick's events in. `world` must be the world right after the tick (the dead units are
   * still in it). Safe to call with every event (non-deaths are ignored) and with repeats.
   */
  ingest(events: readonly SimEvent[], world: Readonly<Pick<World, 'units' | 'dungeonId'>>): TallyTick | null {
    if (!this.ok) return null;
    let deaths: SimEvent[] | null = null;
    for (const e of events) {
      if (e.type === 'death' && !this.seen.has(e.targetID)) (deaths ??= []).push(e);
    }
    if (!deaths) return null;
    let raw: KillRewardSummary;
    try {
      raw = killRewardsFromEvents(deaths, world, this.content, this.keyLevel);
    } catch (err) {
      this.ok = false;
      console.warn('[web] kill rewards failed; progression will estimate them', err);
      return null;
    }
    // Units that died but paid nothing (adds, party) are not re-checked either.
    for (const e of deaths) if (e.targetID) this.seen.add(e.targetID);
    if (!raw.entries.length) return null;
    const before = this.heroView.level;
    const out: KillRewardEntry[] = [];
    let tx = 0;
    let tg = 0;
    for (const en of raw.entries) {
      const npc = this.content.npc(en.npcId);
      if (!npc) continue;
      const r = killReward(npc, this.keyLevel, { finalBoss: en.finalBoss });
      this.exactXp += r.xp;
      this.exactGold += r.gold;
      const dx = roundCum(this.exactXp) - this.xp;
      const dg = roundCum(this.exactGold) - this.gold;
      this.xp += dx;
      this.gold += dg;
      tx += dx;
      tg += dg;
      const entry = { ...en, xp: dx, gold: dg };
      this.entries.push(entry);
      out.push(entry);
    }
    this.heroView = this.computeHero();
    return { entries: out, xp: tx, gold: tg, levelsGained: this.heroView.level - before, hero: this.heroView };
  }

  private computeHero(): HeroProgressView {
    const s = this.start;
    if (!s) return { level: 1, xp: 0, next: xpToNext(1), maxed: false, talentPoints: 1 };
    return provisionalHero(s, this.xp);
  }
}

/** The hero after gaining `xp` (same rules as progression's addHeroXp). */
export function provisionalHero(start: Pick<InRunStart, 'classId' | 'level' | 'xp' | 'talentPoints'>, xp: number): HeroProgressView {
  const ch = { classId: start.classId, hero: { level: start.level, xp: start.xp, talentPoints: start.talentPoints } } as CharacterProgress;
  addHeroXp(ch, Math.max(0, xp));
  const maxed = ch.hero.level >= MAX_HERO_LEVEL;
  return { level: ch.hero.level, xp: ch.hero.xp, next: maxed ? 0 : xpToNext(ch.hero.level), maxed, talentPoints: ch.hero.talentPoints };
}

/** Same rounding as progression's killRewardsFromEvents (epsilon so float 0.4999… rounds up). */
const roundCum = (x: number): number => Math.round(x + 1e-9);
